import PropTypes from "prop-types";
import React, { useState } from "react";
import AdminPageLayout from "../../../../components/FullPageLayout/AdminPageLayout";
import { Tooltip } from "../../../../components/Tooltip";
import {
  Box,
  Typography,
  TextField,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  FormHelperText
} from "@mui/material";
import * as Yup from "yup";
import CourseActivityIcon from "../../../../components/Icons/CourseActivityIcon";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import makeStyles from "@mui/styles/makeStyles";
import Tags from "../../../../components/Tags/Tags";
import EventLog from "./EventLog";
import { NavLink } from "react-router-dom";
import ManageRepositoryHeaderActions from "./ManageRepositoryHeaderActions";
import { useTranslation } from "react-i18next";
import {
  buildRepositoryItemAssociatedCoursesRoute,
  buildRepositoryItemAssociatedFilesRoute,
  buildShowRepositoryItemVersionRoute
} from "../../../../routes/AdminRoutes";
import { formatDate } from "../../../../utils/datetime";
import {
  repositoryItemDurationOptions,
  repositoryItemDurationHours,
  activityTypes,
  audioMimeType,
  htmlMimeType,
  officeMimeTypes,
  pdfMimeType,
  imageFileMimeTypes
} from "../../../../constants";
import { noop } from "../../../../utils/helpers";
import ItemPreviewModal from "./ItemPreviewModal";
import { getDownloadTooltipText } from "../../../../utils/repositoryUtils";
import DualLabelSwitch from "../../../../components/DualLabelSwitch/DualLabelSwitch";
import { Skeleton } from "@mui/material";

const useStyles = makeStyles(theme => ({
  switch: {
    marginLeft: theme.spacing(2)
  },
  textField: {
    marginBottom: theme.spacing(4.5)
  },
  contentIcon: {
    marginRight: theme.spacing(2.25)
  }
}));

const useAssociatedItemsStyles = makeStyles(theme => ({
  linkLabel: {
    minWidth: 180
  },
  link: {
    minWidth: 20,
    textAlign: "center",
    "&:hover": {
      textDecoration: "underline"
    }
  }
}));

const getItemType = (itemType, mimeType) => {
  // Need to check mime type in case item is an audio file because currently an audio file's type is getting returned as "file"
  if (mimeType === audioMimeType) {
    return activityTypes.audio;
  } else if (officeMimeTypes.includes(mimeType)) {
    return activityTypes.document;
  } else if (mimeType === htmlMimeType) {
    return activityTypes.page;
  } else if (mimeType === pdfMimeType) {
    return activityTypes.pdf;
  } else if (imageFileMimeTypes.includes(mimeType)) {
    return activityTypes.image;
  } else if (itemType === activityTypes.link) {
    return activityTypes.link;
  } else {
    return itemType;
  }
};

const getItemSchema = (t, itemType) => {
  return Yup.object().shape({
    name: Yup.string()
      .required(t("validationMessages.nameRequired"))
      .max(
        255,
        t("validationMessages.cannotExceedLength", {
          type: t("addNewContent.addItem.name"),
          maxLength: 255
        })
      ),
    description: Yup.string()
      .nullable()
      .max(
        itemType === activityTypes.homework ? 800 : 350,
        t("validationMessages.cannotExceedLength", {
          type: t("addNewContent.addItem.description"),
          maxLength: itemType === activityTypes.homework ? 800 : 350
        })
      ),
    comment: Yup.string()
      .nullable()
      .max(
        350,
        t("validationMessages.cannotExceedLength", {
          type: t("addNewContent.addItem.comments"),
          maxLength: 350
        })
      ),
    tags: Yup.array().of(
      Yup.string().max(
        250,
        t("validationMessages.cannotExceedLength", {
          type: t("addNewContent.addItem.tags"),
          maxLength: 250
        })
      )
    )
  });
};

const AssociatedItems = ({
  numCourses,
  numFiles,
  repositoryId,
  itemId,
  onGoBack
}) => {
  const classes = useAssociatedItemsStyles();
  const { t } = useTranslation();
  // TODO: Change links to navigate user to pages for associated files/courses
  return (
    <Box display="flex" my={5} flexDirection="column" alignSelf="flex-start">
      <Box display="flex">
        <Typography
          variant="subtitle1"
          color="primary"
          className={classes.linkLabel}
        >
          {t("manageRepository.manageItemAssociatedCourses")}
        </Typography>
        <NavLink
          to={{
            pathname: buildRepositoryItemAssociatedCoursesRoute(
              repositoryId,
              itemId
            ),
            otherProps: { onGoBackClick: onGoBack }
          }}
          className={classes.link}
        >
          <Typography variant="button" color="primary">
            {numCourses}
          </Typography>
        </NavLink>
      </Box>

      {false && (
        <Box display="flex" mt={2}>
          <Typography
            variant="subtitle1"
            color="primary"
            className={classes.linkLabel}
          >
            {t("manageRepository.manageItemAssociatedFiles")}
          </Typography>
          <NavLink
            to={buildRepositoryItemAssociatedFilesRoute(repositoryId, itemId)}
            className={classes.link}
          >
            <Typography variant="button" color="primary">
              {numFiles}
            </Typography>
          </NavLink>
        </Box>
      )}
    </Box>
  );
};

const RepositoryItemDetails = ({
  repositoryItem,
  repository,
  repositoryId,
  itemId,
  updatesDisabled,
  openedAsVersion,
  repositoryAccessType,
  onGoBackClick,
  onReturnHereClick,
  onUpdateData,
  onAddItem,
  onArchive,
  loading
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [validationError, setValidationError] = useState({});
  const itemSchema = getItemSchema(t, repositoryItem?.itemType);

  const handlePreviewItemClick = () => {
    if (
      repositoryItem.itemType === activityTypes.link &&
      repositoryItem.openInNewTab
    ) {
      window.open(repositoryItem.previewUrl, "_blank");
    } else {
      setPreviewModalOpen(true);
    }
  };

  const updateValidationError = (field, message) => {
    const newValidationError = {
      ...validationError,
      [field]: message
    };
    setValidationError(newValidationError);
  };

  const removeValidationError = field => {
    if (validationError[field]) {
      const newValidationError = { ...validationError };
      delete newValidationError[field];
      setValidationError(newValidationError);
    }
  };

  const handleRepositoryItemDataChange = async (field, newValue, skipSave) => {
    removeValidationError(field);
    try {
      await itemSchema.validate({
        name: repositoryItem.name,
        type: repositoryItem.extendedItemType,
        [field]: newValue
      });
      onUpdateData(field, newValue, skipSave);
    } catch (err) {
      updateValidationError(field, err.message);
    }
  };

  const handleTagsDelete = tagToRemove =>
    onUpdateData(
      "tags",
      repositoryItem.tags.filter(tag => tag !== tagToRemove)
    );

  const handleTagsChange = async (e, updatedTags) => {
    removeValidationError("tags");
    try {
      await itemSchema.validate({
        name: repositoryItem.name,
        tags: updatedTags
      });
      onUpdateData("tags", updatedTags);
    } catch (err) {
      updateValidationError("tags", err.message);
    }
  };

  const handleTitleChange = async title => {
    removeValidationError("name");
    try {
      await itemSchema.validate({ name: title });
      onUpdateData("name", title);
    } catch (err) {
      updateValidationError("name", err.message);
    }
  };

  const handleStatusChange = event =>
    onUpdateData("status", event.target.checked ? "active" : "archived");

  const handleEventLogRowClick = rowData => {
    if (itemId === rowData.id) {
      return;
    }
    dispatch(
      push(
        buildShowRepositoryItemVersionRoute(repositoryId, itemId, rowData.id)
      )
    );
  };

  const handleDownload = () => {
    window.open(repositoryItem.downloadLink, "_blank");
  };

  const {
    name,
    createdDate,
    fileName,
    iconType,
    status,
    description,
    durationHours,
    durationMinutes,
    comment,
    isLatestVersion,
    associatedCourses,
    associatedItems,
    associatedWithActiveRelease,
    versions,
    tags,
    extendedItemType,
    mimeType,
    previewUrl,
    downloadLink,
    downloadRule,
    openInNewTab,
    allowIframe
  } = repositoryItem;

  const formattedCreatedDate = formatDate(createdDate, "MMM DD, YYYY");

  const StatusField = () => {
    const disabledMsgKey = updatesDisabled
      ? "itemStatusDisabledCustomer"
      : "itemStatusDisabledCourses";

    const statusDisabledMsg = (
      <Typography
        variant="body2"
        color="textPrimary"
        data-qa="status-disabled-tooltip-text"
      >
        {t(
          isLatestVersion
            ? `components.tooltip.${disabledMsgKey}`
            : "manageRepository.notLatestVersion"
        )}
      </Typography>
    );
    const isActive = status === "active";
    const statusDisabled =
      updatesDisabled || (isActive ? associatedWithActiveRelease : false);

    const dualSwitch = (
      <DualLabelSwitch
        checked={isActive}
        ariaLabel={`${name} status`}
        disabled={statusDisabled}
        onChange={handleStatusChange}
        onLabel="Active"
        offLabel="Inactive"
      />
    );

    if (statusDisabled) {
      return (
        <Tooltip title={statusDisabledMsg}>
          <div>{dualSwitch}</div>
        </Tooltip>
      );
    }
    return dualSwitch;
  };

  const contentType = getItemType(extendedItemType, mimeType);
  // TODO: update to show preview button for other content types once their preview is implemented

  const previewableFileTypes = [
    activityTypes.video,
    activityTypes.audio,
    activityTypes.document,
    activityTypes.pdf,
    activityTypes.page,
    activityTypes.image,
    activityTypes.link,
    activityTypes.scorm
  ];

  const showPreviewItemBtn = previewableFileTypes.includes(contentType);

  return (
    <AdminPageLayout
      title={name}
      titleChangeError={validationError.name}
      headerActions={
        <ManageRepositoryHeaderActions
          onAddItemClick={isLatestVersion ? onAddItem : null}
          onPreviewItemClick={
            showPreviewItemBtn ? handlePreviewItemClick : null
          }
          previewItemDisabled={previewUrl === undefined ? true : false}
          onArchiveClick={isLatestVersion ? onArchive : null}
          searchDisabled={updatesDisabled}
          addItemDisabled={
            repositoryItem.repository
              ? !repositoryItem.repository.allowItemsManagement
              : true
          }
          onDownloadClick={handleDownload}
          downloadDisabled={!downloadLink}
          downloadTooltipText={t(
            getDownloadTooltipText(true, !!downloadLink, downloadRule)
          )}
          searchVisible={false}
        />
      }
      editableTitle={!updatesDisabled}
      onTitleChange={handleTitleChange}
      onGoBackClick={onGoBackClick}
    >
      <Box display="flex" flexDirection="column" mt={4}>
        {loading ? (
          <Skeleton height={400} variant="rectangular" />
        ) : (
          <>
            <Box display="flex" alignItems="center">
              <div className={classes.contentIcon}>
                <CourseActivityIcon type={iconType} color="action" />
              </div>
              <Typography variant="subtitle2">{`${formattedCreatedDate} \u00A0|\u00A0 ${
                fileName ? fileName : name
              } \u00A0|\u00A0 ${
                repository ? repository.name : ""
              }`}</Typography>
            </Box>
            <Box display="flex" mt={4} alignItems="center" mb={4}>
              <StatusField />
            </Box>
            {contentType === "link" && (
              <Box alignItems="center" mb={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={openInNewTab}
                      onChange={event =>
                        handleRepositoryItemDataChange(
                          "openInNewTab",
                          !openInNewTab
                        )
                      }
                      value={openInNewTab}
                    />
                  }
                  label={t("courseBuilder.addLinkOpenNewTabField")}
                />
                {allowIframe === false && (
                  <FormHelperText variant="filled">
                    {t("courseBuilder.invalidIframeHelperText")}
                  </FormHelperText>
                )}
              </Box>
            )}
            <TextField
              label={t("manageRepository.manageItemDescription")}
              rows="3"
              margin="normal"
              defaultValue={description}
              variant="filled"
              fullWidth
              multiline
              onChange={event =>
                handleRepositoryItemDataChange(
                  "description",
                  event.target.value,
                  true
                )
              }
              className={classes.textField}
              disabled={updatesDisabled}
              error={!!validationError.description}
              helperText={validationError.description}
              onBlur={event => {
                handleRepositoryItemDataChange(
                  "description",
                  event.target.value
                );
              }}
            />
            <Box display="flex" alignItems="center" mb={4.5}>
              <Typography variant="subtitle1">
                {t("manageRepository.manageItemDuration")}
              </Typography>
              <Box ml={3} width={100}>
                <FormControl disabled={updatesDisabled} fullWidth>
                  <InputLabel htmlFor="duration-hours" shrink={true}>
                    {t("manageRepository.manageItemDurationHours")}
                  </InputLabel>
                  <Select
                    native
                    variant="filled"
                    value={durationHours}
                    onChange={event =>
                      handleRepositoryItemDataChange(
                        "durationHours",
                        parseInt(event.target.value)
                      )
                    }
                    inputProps={{
                      id: "duration-hours"
                    }}
                  >
                    {repositoryItemDurationHours.map(hour => (
                      <option value={hour} key={hour}>
                        {hour}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box ml={3} width={100}>
                <FormControl disabled={updatesDisabled} fullWidth>
                  <InputLabel htmlFor="duration-minutes" shrink={true}>
                    {t("manageRepository.manageItemDurationMinutes")}
                  </InputLabel>
                  <Select
                    native
                    variant="filled"
                    value={durationMinutes}
                    onChange={event =>
                      handleRepositoryItemDataChange(
                        "durationMinutes",
                        parseInt(event.target.value)
                      )
                    }
                    inputProps={{
                      id: "duration-minutes"
                    }}
                  >
                    {repositoryItemDurationOptions.map(minute => (
                      <option value={minute} key={minute}>
                        {minute}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Tags
              tags={tags}
              label={t("manageRepository.manageItemTags")}
              addLabel={t("manageRepository.addTag")}
              onDelete={handleTagsDelete}
              onChange={handleTagsChange}
              disabled={updatesDisabled}
              error={validationError.tags}
            />
            <AssociatedItems
              repositoryId={repositoryId}
              itemId={itemId}
              numCourses={associatedCourses ? associatedCourses.length : 0}
              numFiles={associatedItems ? associatedItems.length : 0}
              onGoBack={onReturnHereClick}
            />
            {repositoryAccessType === "owned" && (
              <TextField
                label={t("manageRepository.manageItemComments")}
                rows="3"
                margin="normal"
                defaultValue={comment || ""}
                variant="filled"
                fullWidth
                multiline
                onChange={event =>
                  handleRepositoryItemDataChange(
                    "comment",
                    event.target.value,
                    true
                  )
                }
                className={classes.textField}
                error={!!validationError.comment}
                helperText={validationError.comment}
                onBlur={event =>
                  handleRepositoryItemDataChange("comment", event.target.value)
                }
                disabled={updatesDisabled}
              />
            )}
            {repositoryAccessType === "owned" && !updatesDisabled && (
              <Box>
                <Typography variant="subtitle1" color="primary">
                  {t("manageRepository.manageItemCurrentVersionEditMsg")}
                </Typography>
              </Box>
            )}
            {!openedAsVersion && (
              <EventLog
                data={versions}
                onRowClick={handleEventLogRowClick}
                showInternalInformation={repositoryAccessType === "owned"}
              />
            )}
          </>
        )}
      </Box>
      <ItemPreviewModal
        itemName={name}
        itemType={contentType}
        itemMimeType={mimeType}
        previewUrl={previewUrl}
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
      />
    </AdminPageLayout>
  );
};

export default RepositoryItemDetails;

RepositoryItemDetails.propTypes = {
  repositoryItem: PropTypes.any,
  repository: PropTypes.any,
  repositoryId: PropTypes.string,
  itemId: PropTypes.string,
  updatesDisabled: PropTypes.bool,
  openedAsVersion: PropTypes.bool,
  repositoryAccessType: PropTypes.string,
  onGoBackClick: PropTypes.func,
  onReturnHereClick: PropTypes.func,
  /*
    What to do if the field is updated
    onUpdateData(fieldName, value, skipSaveToDb)
  */
  onUpdateData: PropTypes.func,
  onArchive: PropTypes.func,
  onAddItem: PropTypes.func,
  error: PropTypes.any,
  loading: PropTypes.bool
};

RepositoryItemDetails.defaultProps = {
  updatesDisabled: true,
  openedAsVersion: false,
  onUpdateData: noop
};
